import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';

const Login = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { login } = useAuth();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await api.post('/auth/login', { username, password });
      const { user } = response.data;
      
      login(user);
      navigate('/');
    } catch (err) {
      setError(err.response?.data?.message || 'Login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const styles = {
    container: {
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'linear-gradient(135deg, #004b87 0%, #0066b3 50%, #1a8fcc 100%)',
      fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
      padding: '20px',
      position: 'relative',
      overflow: 'hidden',
    },
    backgroundPattern: {
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      opacity: 0.05,
      backgroundImage: `
        repeating-linear-gradient(45deg, transparent, transparent 35px, rgba(255,255,255,.08) 35px, rgba(255,255,255,.08) 70px),
        repeating-linear-gradient(-45deg, transparent, transparent 35px, rgba(255,255,255,.05) 35px, rgba(255,255,255,.05) 70px)
      `,
    },
    decorCircle1: {
      position: 'absolute',
      top: '-10%',
      right: '-5%',
      width: '500px',
      height: '500px',
      borderRadius: '50%',
      background: 'radial-gradient(circle, rgba(26, 143, 204, 0.3) 0%, transparent 70%)',
      filter: 'blur(60px)',
    },
    decorCircle2: {
      position: 'absolute',
      bottom: '-15%',
      left: '-10%',
      width: '600px',
      height: '600px',
      borderRadius: '50%',
      background: 'radial-gradient(circle, rgba(0, 75, 135, 0.4) 0%, transparent 70%)',
      filter: 'blur(80px)',
    },
    content: {
      position: 'relative',
      zIndex: 1,
      width: '100%',
      maxWidth: '1200px',
      display: 'grid',
      gridTemplateColumns: '1fr 1fr',
      gap: '80px',
      alignItems: 'center',
    },
    branding: {
      color: 'white',
      animation: 'fadeInLeft 0.8s ease-out',
    },
    logoContainer: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'flex-start',
      marginBottom: '40px',
    },
    logo: {
      width: '180px',
      height: '180px',
      marginBottom: '25px',
      filter: 'drop-shadow(0 10px 30px rgba(0,0,0,0.3))',
    },
    brandTitle: {
      fontSize: '52px',
      fontWeight: '900',
      margin: '0 0 15px 0',
      letterSpacing: '-1.5px',
      textShadow: '3px 3px 6px rgba(0,0,0,0.4)',
      lineHeight: 1.1,
    },
    tagline: {
      fontSize: '20px',
      opacity: 0.95,
      marginBottom: '25px',
      fontWeight: '300',
      letterSpacing: '0.5px',
    },
    divider: {
      height: '3px',
      width: '100%',
      background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.6), rgba(255, 255, 255, 0.3), transparent)',
      marginBottom: '35px',
    },
    systemTitle: {
      fontSize: '26px',
      fontWeight: '700',
      opacity: 0.95,
      letterSpacing: '0.3px',
      marginBottom: '10px',
    },
    systemSubtitle: {
      fontSize: '16px',
      opacity: 0.85,
      fontWeight: '300',
      lineHeight: 1.6,
    },
    card: {
      background: 'rgba(255, 255, 255, 0.98)',
      borderRadius: '24px',
      padding: '55px 50px',
      boxShadow: '0 25px 80px rgba(0, 0, 0, 0.4)',
      backdropFilter: 'blur(20px)',
      animation: 'fadeInUp 0.8s ease-out',
      border: '1px solid rgba(255, 255, 255, 0.2)',
    },
    cardHeader: {
      marginBottom: '40px',
      textAlign: 'center',
    },
    cardTitle: {
      fontSize: '34px',
      fontWeight: '700',
      color: '#004b87',
      marginBottom: '10px',
    },
    cardSubtitle: {
      fontSize: '15px',
      color: '#5a6c7d',
    },
    form: {
      display: 'flex',
      flexDirection: 'column',
      gap: '25px',
    },
    formGroup: {
      display: 'flex',
      flexDirection: 'column',
      gap: '10px',
    },
    label: {
      fontSize: '14px',
      fontWeight: '600',
      color: '#004b87',
      marginLeft: '4px',
    },
    inputWrapper: {
      position: 'relative',
      display: 'flex',
      alignItems: 'center',
    },
    inputIcon: {
      position: 'absolute',
      left: '18px',
      width: '22px',
      height: '22px',
      color: '#0066b3',
      opacity: 0.6,
      pointerEvents: 'none',
      zIndex: 1,
    },
    input: {
      width: '100%',
      padding: '16px 18px 16px 52px',
      border: '2px solid #e0e7ef',
      borderRadius: '14px',
      fontSize: '15px',
      fontFamily: 'inherit',
      transition: 'all 0.3s ease',
      outline: 'none',
      backgroundColor: '#fff',
    },
    inputFocus: {
      borderColor: '#0066b3',
      boxShadow: '0 0 0 4px rgba(0, 102, 179, 0.1)',
    },
    button: {
      width: '100%',
      padding: '18px',
      background: 'linear-gradient(135deg, #1a8fcc 0%, #0066b3 100%)',
      color: 'white',
      border: 'none',
      borderRadius: '14px',
      fontSize: '17px',
      fontWeight: '700',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      marginTop: '15px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '10px',
      boxShadow: '0 6px 20px rgba(0, 102, 179, 0.35)',
      textTransform: 'uppercase',
      letterSpacing: '0.5px',
    },
    buttonHover: {
      transform: 'translateY(-3px)',
      boxShadow: '0 10px 30px rgba(0, 102, 179, 0.45)',
      background: 'linear-gradient(135deg, #1a8fcc 0%, #004b87 100%)',
    },
    buttonDisabled: {
      opacity: 0.7,
      cursor: 'not-allowed',
    },
    spinner: {
      width: '20px',
      height: '20px',
      border: '3px solid rgba(255, 255, 255, 0.3)',
      borderTop: '3px solid white',
      borderRadius: '50%',
      animation: 'spin 0.8s linear infinite',
    },
    alert: {
      padding: '16px 18px',
      borderRadius: '12px',
      fontSize: '14px',
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      backgroundColor: '#fee',
      color: '#c00',
      border: '1px solid #fcc',
    },
    alertIcon: {
      width: '22px',
      height: '22px',
      flexShrink: 0,
    },
    footer: {
      marginTop: '35px',
      textAlign: 'center',
      fontSize: '15px',
      color: '#5a6c7d',
    },
    link: {
      color: '#0066b3',
      textDecoration: 'none',
      fontWeight: '700',
      transition: 'color 0.3s ease',
    },
    infoGrid: {
      display: 'grid',
      gap: '22px',
      marginTop: '45px',
      animation: 'fadeInLeft 1s ease-out',
    },
    infoCard: {
      display: 'flex',
      gap: '22px',
      padding: '28px',
      background: 'rgba(255, 255, 255, 0.12)',
      backdropFilter: 'blur(10px)',
      borderRadius: '18px',
      border: '1px solid rgba(255, 255, 255, 0.25)',
      transition: 'all 0.4s ease',
    },
    infoCardHover: {
      background: 'rgba(255, 255, 255, 0.18)',
      transform: 'translateX(8px)',
    },
    infoIcon: {
      width: '52px',
      height: '52px',
      padding: '13px',
      background: 'rgba(255, 255, 255, 0.2)',
      borderRadius: '14px',
      color: 'white',
      flexShrink: 0,
    },
    infoContent: {
      flex: 1,
    },
    infoTitle: {
      fontSize: '19px',
      fontWeight: '700',
      marginBottom: '8px',
      color: 'white',
    },
    infoText: {
      fontSize: '14px',
      opacity: 0.9,
      lineHeight: 1.6,
      color: 'rgba(255, 255, 255, 0.85)',
    },
  };

  // Add keyframe animations
  React.useEffect(() => {
    const styleSheet = document.styleSheets[0];
    const keyframes = `
      @keyframes fadeInLeft {
        from {
          opacity: 0;
          transform: translateX(-40px);
        }
        to {
          opacity: 1;
          transform: translateX(0);
        }
      }
      @keyframes fadeInUp {
        from {
          opacity: 0;
          transform: translateY(40px);
        }
        to {
          opacity: 1;
          transform: translateY(0);
        }
      }
      @keyframes spin {
        to { transform: rotate(360deg); }
      }
    `;
    
    try {
      styleSheet.insertRule(keyframes, styleSheet.cssRules.length);
    } catch (e) {}
  }, []);

  return (
    <div style={styles.container}>
      <div style={styles.backgroundPattern}></div>
      <div style={styles.decorCircle1}></div>
      <div style={styles.decorCircle2}></div>
      
      <div style={styles.content}>
        <div style={styles.branding}>
          <div style={styles.logoContainer}>
            
             <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '20px', // space between logo and title
              marginBottom: '25px',
            }}>
              {/* WebP Logo from public folder */}
              <img 
                src={`${process.env.PUBLIC_URL}/ML-Logo.png`}
                alt="Miller & Long Logo" 
                style={{
                  width: '180px',
                  height: '180px',
                  objectFit: 'contain',
                  filter: 'drop-shadow(0 10px 30px rgba(0,0,0,0.3))',
                }} 
              />

              <h1 style={styles.brandTitle}>Miller & Long</h1>
            </div>
          </div>
          <p style={styles.tagline}>Building DC's Future Since 1947</p>
          <div style={styles.divider}></div>
          <h2 style={styles.systemTitle}>Production Management System</h2>
          <p style={styles.systemSubtitle}>Empowering teams with data-driven insights for superior concrete construction</p>

          <div style={styles.infoGrid}>
            <div 
              style={styles.infoCard}
              onMouseEnter={(e) => Object.assign(e.currentTarget.style, styles.infoCardHover)}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.12)';
                e.currentTarget.style.transform = 'translateX(0)';
              }}
            >
              <svg style={styles.infoIcon} viewBox="0 0 20 20" fill="currentColor">
                <path d="M9 6a3 3 0 11-6 0 3 3 0 016 0zM17 6a3 3 0 11-6 0 3 3 0 016 0zM12.93 17c.046-.327.07-.66.07-1a6.97 6.97 0 00-1.5-4.33A5 5 0 0119 16v1h-6.07zM6 11a5 5 0 015 5v1H1v-1a5 5 0 015-5z" />
              </svg>
              <div style={styles.infoContent}>
                <h4 style={styles.infoTitle}>Team Collaboration</h4>
                <p style={styles.infoText}>Real-time production tracking across all project sites throughout the Mid-Atlantic</p>
              </div>
            </div>
            <div 
              style={styles.infoCard}
              onMouseEnter={(e) => Object.assign(e.currentTarget.style, styles.infoCardHover)}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.12)';
                e.currentTarget.style.transform = 'translateX(0)';
              }}
            >
              <svg style={styles.infoIcon} viewBox="0 0 20 20" fill="currentColor">
                <path d="M2 11a1 1 0 011-1h2a1 1 0 011 1v5a1 1 0 01-1 1H3a1 1 0 01-1-1v-5zM8 7a1 1 0 011-1h2a1 1 0 011 1v9a1 1 0 01-1 1H9a1 1 0 01-1-1V7zM14 4a1 1 0 011-1h2a1 1 0 011 1v12a1 1 0 01-1 1h-2a1 1 0 01-1-1V4z" />
              </svg>
              <div style={styles.infoContent}>
                <h4 style={styles.infoTitle}>Data-Driven Insights</h4>
                <p style={styles.infoText}>Advanced analytics and precise estimation tools for optimal project delivery</p>
              </div>
            </div>
          </div>
        </div>

        <div style={styles.card}>
          <div style={styles.cardHeader}>
            <h3 style={styles.cardTitle}>Welcome Back</h3>
            <p style={styles.cardSubtitle}>Sign in to access your production dashboard</p>
          </div>

          <form onSubmit={handleSubmit} style={styles.form}>
            {error && (
              <div style={styles.alert}>
                <svg style={styles.alertIcon} viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
                {error}
              </div>
            )}
            
            <div style={styles.formGroup}>
              <label style={styles.label}>Username</label>
              <div style={styles.inputWrapper}>
                <svg style={styles.inputIcon} viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                </svg>
                <input
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder="Enter your username"
                  required
                  autoFocus
                  style={styles.input}
                  onFocus={(e) => Object.assign(e.target.style, styles.inputFocus)}
                  onBlur={(e) => {
                    e.target.style.borderColor = '#e0e7ef';
                    e.target.style.boxShadow = 'none';
                  }}
                />
              </div>
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>Password</label>
              <div style={styles.inputWrapper}>
                <svg style={styles.inputIcon} viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                </svg>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  required
                  style={styles.input}
                  onFocus={(e) => Object.assign(e.target.style, styles.inputFocus)}
                  onBlur={(e) => {
                    e.target.style.borderColor = '#e0e7ef';
                    e.target.style.boxShadow = 'none';
                  }}
                />
              </div>
            </div>

            <button 
              type="submit" 
              style={{...styles.button, ...(loading && styles.buttonDisabled)}}
              disabled={loading}
              onMouseEnter={(e) => !loading && Object.assign(e.target.style, styles.buttonHover)}
              onMouseLeave={(e) => {
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = '0 6px 20px rgba(0, 102, 179, 0.35)';
                e.target.style.background = 'linear-gradient(135deg, #1a8fcc 0%, #0066b3 100%)';
              }}
            >
              {loading ? (
                <>
                  <span style={styles.spinner}></span>
                  Signing in...
                </>
              ) : (
                'Sign In'
              )}
            </button>
          </form>

          <div style={styles.footer}>
            <p>Don't have an account? <Link to="/register" style={styles.link}>Create Account</Link></p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;